import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog, TagPill } from '@aibuild/ui';
import type { AppCatalogEntry } from '@api/apps';
import AppCard from '../AppCard';
import { isBuiltInApp, isSharedInOrg, isSharedViaLink } from '../appTabs';
import { localized } from '../appRunView';
import { CardGrid } from '../AppsGalleryPage.styled';
import AppTile from './AppTile';
import CreateAppCard from './CreateAppCard';
import CreateAppDialog from './create/CreateAppDialog';
import { useMyApps } from './useAppBuilder';
import { useGalleryActions } from './useGalleryActions';
import { builderPath, isRunnable, userAppStatusLabel } from './userAppStatus';
import { Section, SectionNote, SectionTitle, Sections } from './AppGallerySections.styled';

interface AppGallerySectionsProps {
  readonly catalog: readonly AppCatalogEntry[];
  readonly onOpenApp: (appKey: string) => void;
}

const AppGallerySections: React.FC<AppGallerySectionsProps> = ({ catalog, onOpenApp }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const mine = useMyApps(true);
  const menus = useGalleryActions();
  const [creating, setCreating] = useState(false);

  const shared = catalog.filter(isSharedInOrg);
  const linked = catalog.filter(isSharedViaLink);
  const builtIns = catalog.filter(isBuiltInApp);

  const menuLabel = (name: string) =>
    intl.formatMessage(
      { id: 'apps.builder.action.menu', defaultMessage: 'Actions for {name}' },
      { name },
    );

  const publicLinkPill = (appKey: string) => (
    <TagPill
      label={intl.formatMessage({ id: 'apps.gallery.publicLink', defaultMessage: 'Public link' })}
      prefix={false}
      size="sm"
      dataTestId={`apps-gallery__public-${appKey}`}
    />
  );

  return (
    <Sections>
      <Section aria-labelledby="apps-gallery__section-mine">
        <SectionTitle id="apps-gallery__section-mine">
          {intl.formatMessage({ id: 'apps.gallery.section.mine', defaultMessage: 'My apps' })}
        </SectionTitle>
        {mine.isError && (
          <SectionNote>
            {intl.formatMessage({
              id: 'apps.gallery.section.mine.error',
              defaultMessage: 'Your apps could not be loaded. Reload the page to try again.',
            })}
          </SectionNote>
        )}
        <CardGrid data-testid="apps-gallery__mine">
          <CreateAppCard onClick={() => setCreating(true)} />
          {(mine.data ?? []).map((app) => (
            <AppTile
              key={app.appKey}
              appKey={app.appKey}
              manifest={app.manifest}
              onOpen={() => (isRunnable(app) ? onOpenApp(app.appKey) : navigate(builderPath(app.appKey)))}
              menuLabel={menuLabel(localized(intl, app.manifest.name))}
              actions={menus.forMine(app)}
              footer={
                <>
                  <TagPill
                    label={userAppStatusLabel(intl, app)}
                    prefix={false}
                    size="sm"
                    dataTestId={`apps-gallery__status-${app.appKey}`}
                  />
                  {app.publicLink && publicLinkPill(app.appKey)}
                  {isRunnable(app) && app.hasUnpublishedChanges && (
                    <span>
                      {intl.formatMessage({
                        id: 'apps.builder.status.unpublishedChanges',
                        defaultMessage: 'Unpublished changes',
                      })}
                    </span>
                  )}
                </>
              }
            />
          ))}
        </CardGrid>
      </Section>

      {shared.length > 0 && (
        <Section aria-labelledby="apps-gallery__section-shared">
          <SectionTitle id="apps-gallery__section-shared">
            {intl.formatMessage({
              id: 'apps.gallery.section.shared',
              defaultMessage: 'Shared in your organization',
            })}
          </SectionTitle>
          <CardGrid data-testid="apps-gallery__shared">
            {shared.map((entry) => (
              <AppTile
                key={entry.appKey}
                appKey={entry.appKey}
                manifest={entry.manifest}
                onOpen={() => onOpenApp(entry.appKey)}
                menuLabel={menuLabel(localized(intl, entry.manifest.name))}
                actions={menus.forShared(entry)}
                footer={
                  <>
                    {entry.publicLink && publicLinkPill(entry.appKey)}
                    {entry.createdByName && (
                      <span data-testid={`apps-gallery__creator-${entry.appKey}`}>
                        {intl.formatMessage(
                          { id: 'apps.gallery.createdBy', defaultMessage: 'Created by {name}' },
                          { name: entry.createdByName },
                        )}
                      </span>
                    )}
                  </>
                }
              />
            ))}
          </CardGrid>
        </Section>
      )}

      {linked.length > 0 && (
        <Section aria-labelledby="apps-gallery__section-linked">
          <SectionTitle id="apps-gallery__section-linked">
            {intl.formatMessage({ id: 'apps.gallery.section.linked', defaultMessage: 'Shared with you' })}
          </SectionTitle>
          <CardGrid data-testid="apps-gallery__linked">
            {linked.map((entry) => (
              <AppCard
                key={entry.appKey}
                appKey={entry.appKey}
                manifest={entry.manifest}
                onOpen={() => onOpenApp(entry.appKey)}
              />
            ))}
          </CardGrid>
        </Section>
      )}

      {builtIns.length > 0 && (
        <Section aria-labelledby="apps-gallery__section-builtin">
          <SectionTitle id="apps-gallery__section-builtin">
            {intl.formatMessage({
              id: 'apps.gallery.section.builtin',
              defaultMessage: 'Built-in apps',
            })}
          </SectionTitle>
          <CardGrid data-testid="apps-gallery__grid">
            {builtIns.map((entry) => (
              <AppCard
                key={entry.appKey}
                appKey={entry.appKey}
                manifest={entry.manifest}
                onOpen={() => onOpenApp(entry.appKey)}
              />
            ))}
          </CardGrid>
        </Section>
      )}

      <CreateAppDialog open={creating} onClose={() => setCreating(false)} />

      <ConfirmDialog
        open={menus.pendingDelete !== null}
        onClose={menus.cancelDelete}
        onConfirm={menus.confirmDelete}
        title={intl.formatMessage({
          id: 'apps.builder.delete.title',
          defaultMessage: 'Delete this app?',
        })}
        description={intl.formatMessage({
          id: 'apps.builder.delete.body',
          defaultMessage:
            'Nobody will be able to start it again, including people you shared it with, and its public link stops working. Runs already made in your organization keep working.',
        })}
        dataTestId="apps-gallery__delete-dialog"
      />
    </Sections>
  );
};

export default AppGallerySections;
